import React from 'react';
import { ArrowDownIcon, ArrowUpIcon } from '@heroicons/react/20/solid';
import InfoTooltip from '../tooltip/InfoTooltip';
import { classNames } from '../utils/classNames';
import { ChangeFormat, FormatOptions, ValueFormat, formatChange, formatSigned, formatValue } from './chartFormat';
import Sparkline from './Sparkline';

// One number with a label, for the top row of a report: an ADR, an occupancy, a price position.
// The anatomy is the anatomy of GeneralStatIndicatorPortlet: the same card, label and number.
// An optional change shows the direction (an arrow and a word for a screen reader), the size, and
// the period of the comparison ("vs last year"). The colour of the change says good or bad: the
// caller says if up is good. With upIsGood = null, the change is gray.
// An optional trend draws a small line of the last values (oldest first); the last step is blue.
// A null value shows '–'. The tile knows nothing about the data behind the number.
// With onClick, the whole tile is a button (one tile of a grid that selects a row, a chart, a filter): the border turns
// gray-300 on hover, and a selected tile has a blue-600 border and ring. A button covers the card, so the definition
// list stays valid HTML; the (i) of the label stays on top of it.

export type KpiChange = {
	// The difference. Its sign is the direction.
	value: number,
	format: ChangeFormat,
	// The period of the comparison, for example "vs last year".
	label: string,
	// true (default): up is good, green. false: down is good. null: no good or bad, gray.
	upIsGood?: boolean | null,
};

type Props = {
	// Sentence case, no colon: "ADR next 30 days".
	label: string,
	value: number | null,
	format: ValueFormat,
	// An ISO 4217 code for 'money'. Default 'EUR'.
	currency?: string,
	// The decimals of the value. Default 0.
	decimals?: number,
	// Show + before a positive value: a position or a difference, for example +6.8%.
	signed?: boolean,
	change?: KpiChange,
	// The last values, oldest first. The last value is the current one.
	trend?: (number | null)[],
	// An (i) after the label with the definition of the number.
	info?: { title: string, content: React.ReactNode },
	state?: 'default' | 'loading',
	// The tile becomes a button. Not active while the tile loads.
	onClick?: () => void,
	// With onClick: the tile is the selected one (aria-pressed).
	selected?: boolean,
};

const Trend: React.FunctionComponent<{ values: (number | null)[] }> = ({ values }) =>
	<Sparkline values={values} className="mt-2 block h-7 w-full" />;

const Change: React.FunctionComponent<{ change: KpiChange, options: FormatOptions }> = ({ change, options }) => {
	const up = change.value > 0;
	const down = change.value < 0;
	const upIsGood = change.upIsGood === undefined ? true : change.upIsGood;
	const good = upIsGood === null || (!up && !down) ? null : (up === upIsGood);
	const tone = good === null ? 'text-gray-600' : (good ? 'text-green-700' : 'text-red-700');
	const Icon = up ? ArrowUpIcon : ArrowDownIcon;
	return <p className="mt-1 flex flex-wrap items-center gap-x-1 text-sm">
		<span className={classNames('inline-flex items-center gap-x-0.5 font-medium', tone)}>
			{(up || down) && <Icon aria-hidden="true" className="h-4 w-4 shrink-0" />}
			<span className="sr-only">{up ? 'Up' : (down ? 'Down' : 'No change,')}</span>
			{formatChange(change.value, change.format, options)}
		</span>
		<span className="text-gray-500">{change.label}</span>
	</p>;
};

const KpiTile: React.FunctionComponent<Props> = ({
	label, value, format, currency, decimals = 0, signed = false, change, trend, info, state = 'default', onClick, selected = false,
}) => {
	const isLoading = state === 'loading';
	const clickable = onClick !== undefined;
	return <dl aria-busy={isLoading} className={classNames('rounded-lg border bg-white px-4 py-3 shadow-sm sm:px-5 sm:py-4',
		clickable ? 'relative' : '',
		clickable && selected ? 'border-blue-600 ring-1 ring-blue-600' : (clickable && !isLoading ? 'border-gray-200 hover:border-gray-300' : 'border-gray-200'))}>
		{clickable && <button type="button" aria-label={label} aria-pressed={selected} disabled={isLoading} onClick={onClick}
			className="absolute inset-0 rounded-lg focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600" />}
		<dt className="flex min-w-0 items-center gap-x-1">
			<span className="truncate text-sm font-semibold text-gray-500">{label}</span>
			{info !== undefined && (clickable
				? <span className="relative z-10 inline-flex"><InfoTooltip label={`About ${label}`} title={info.title}>{info.content}</InfoTooltip></span>
				: <InfoTooltip label={`About ${label}`} title={info.title}>{info.content}</InfoTooltip>)}
		</dt>
		{isLoading
			? <dd className="animate-pulse">
				<span className="sr-only">Loading</span>
				<div className="mt-2 h-7 w-28 rounded bg-gray-200" />
				<div className="mt-2 h-4 w-36 rounded bg-gray-100" />
			</dd>
			: <dd>
				<p className="mt-1 text-2xl font-bold tracking-tight text-gray-900">{(signed ? formatSigned : formatValue)(value, format, { currency, decimals })}</p>
				{change !== undefined && <Change change={change} options={{ currency, decimals: change.format === 'money' || change.format === 'number' ? decimals : undefined }} />}
				{trend !== undefined && <Trend values={trend} />}
			</dd>}
	</dl>;
};

export default KpiTile;
